import { QueryClient } from '@tanstack/react-query';

export function createQueryClient(): QueryClient {
  const client = new QueryClient({ defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } } });
  // Only task/search reads and their container dependencies adopt immediate refresh.
  for (const key of [queryKeys.tasks, queryKeys.searches, queryKeys.containers]) {
    client.setQueryDefaults(key, { staleTime: 0 });
  }
  return client;
}

/**
 * The most rows a list asks for. The API returns at most 1,000 rows per request
 * (max_rows in supabase/config.toml), so lists ask for exactly that and say so
 * when they reach it, rather than stopping silently.
 */
export const LIST_LIMIT = 1000;

/** True when a list came back full, so there may be more than it shows. */
export const reachedLimit = (rows: readonly unknown[] | undefined) => (rows?.length ?? 0) >= LIST_LIMIT;

/**
 * React Query keys shared across pages. Keys nest, so invalidating a prefix
 * refreshes every list under it (['tasks'] refreshes Today, All tasks and
 * every container's tasks).
 */
export const queryKeys = {
  captures: ['captures'],
  inbox: ['captures', 'inbox'],
  unfiledCaptures: ['captures', 'unfiled'],
  sourceCaptures: (kind: string, itemId: string) => ['captures', 'source', kind, itemId],
  tasks: ['tasks'],
  todayTasks: ['tasks', 'today'],
  taskList: ['tasks', 'list'],
  task: (taskId: string) => ['tasks', 'one', taskId],
  /** Under the task, so refreshing it refreshes this too; keyed by the reminder's time, which names its notification. */
  reminderDelivery: (taskId: string, remindAt: string) => ['tasks', 'one', taskId, 'reminder-delivery', remindAt],
  containerTasks: (containerId: string) => ['tasks', 'container', containerId],
  notes: ['notes'],
  containerNotes: (containerId: string) => ['notes', 'container', containerId],
  note: (noteId: string) => ['notes', noteId],
  containers: ['containers'],
  containerList: (table: string) => ['containers', 'list', table],
  container: (table: string, id: string) => ['containers', table, id],
  digests: ['digests'],
  latestDigest: ['digests', 'latest'],
  digestList: ['digests', 'list'],
  people: ['people'],
  outcomes: ['outcomes'],
  /** Under outcomes, so every change to the list asks for the scoring status again. */
  judgmentOverview: ['outcomes', 'judgment-overview'],
  /** Under outcomes too: a changed list re-scores everything. */
  taskJudgments: ['outcomes', 'task-judgments'],
  searches: ['search'],
  peopleList: (search: string) => ['people', 'list', search],
  person: (personId: string) => ['people', 'one', personId],
  /** Under tasks, so every task change refreshes a person's follow-ups (CER-140). */
  personFollowUps: (personId: string) => ['tasks', 'person', personId],
  profile: ['profile'],
  feedback: ['feedback'],
  homeWarnings: ['home-warnings'],
  systemHealth: ['system-health'],
  /** Under it: every connector's roots and the folder picker's lists, refreshed with it. */
  connectors: ['connectors'],
  connectorRoots: ['connectors', 'roots'],
  /** A pairing a computer is waiting on, looked up by the code it shows. */
  connectorPairing: (userCode: string) => ['connectors', 'pairing', userCode],
  pickerFolders: (connectorId: string, rootKey: string, scanAt: string, parent: string | null) =>
    ['connectors', 'picker', connectorId, rootKey, scanAt, parent ?? ''],
  folderBindings: (kind: string, entityId: string) => ['folder-bindings', kind, entityId],
  allFolderBindings: ['folder-bindings'],
} as const;

/** Refresh everything saving or filing a capture, or deleting a task or note, can change: the inbox, tasks, notes, containers and people. */
export function refreshFilingViews(queryClient: QueryClient): Promise<unknown> {
  return Promise.all(
    [queryKeys.captures, queryKeys.tasks, queryKeys.notes, queryKeys.containers, queryKeys.people, queryKeys.searches].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  );
}
